import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BaseForm } from '@forms/base.form';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { WorkSchedule } from '@models/time-off-v3/works-schedule.model';
import { BlackoutPeriodsTable } from '@time-off-v3/services/blackout-periods-table/blackout-periods-table';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { BlackoutPeriodForm } from '@time-off-v3/forms';
import { TimeOffRequestBlackoutPeriod } from '@models/time-off-v3/time-off-request-blackout-period.model';
import { AnalyticService, NotifyService } from '@app/services';
import { AnalyticEvents } from '@app/enums';
import moment from 'moment';

@Component({
    selector: 'app-blackout-periods-form',
    templateUrl: './blackout-periods.template.html',
    styleUrls: ['./blackout-periods.style.scss'],
})
export class BlackoutPeriodsForm extends BaseForm implements OnInit {
    @ViewChild('blackoutPeriodModal') blackoutPeriodModal: DeprecatedModalFormComponent;
    @ViewChild('blackoutPeriodDeleteModal') blackoutPeriodDeleteModal: DeprecatedConfirmationDialogComponent;
    @Input() workSchedule: WorkSchedule;
    @Input() hasTimeOffImprovement = false;

    blackoutPeriodsTable: BlackoutPeriodsTable;

    constructor(
        private analyticService: AnalyticService,
        private notify: NotifyService
    ) {
        super();
    }

    get year(): number {
        return this.blackoutPeriodsTable.activeYear;
    }

    ngOnInit(): void {
        this.blackoutPeriodsTable = new BlackoutPeriodsTable(this.workSchedule);
    }

    showBlackoutPeriodModal(): void {
        this.blackoutPeriodModal.show();
    }

    async menuAction(event: MenuColumnEvent, blackoutPeriodForm: BlackoutPeriodForm): Promise<void> {
        const { menuItem, data } = event;
        const blackoutPeriod = data as TimeOffRequestBlackoutPeriod;

        if (menuItem.event === 'edit') {
            this.handleUpdateMenuAction(blackoutPeriodForm, blackoutPeriod);
            return;
        }

        await this.handleDeleteMenuAction(blackoutPeriod);
    }

    async onSave(blackoutPeriodForm: BlackoutPeriodForm): Promise<void> {
        const blackoutPeriod = blackoutPeriodForm.timeOffRequestBlackoutPeriod;
        blackoutPeriod.workScheduleId = this.workSchedule.id;

        try {
            await blackoutPeriod.save();
            this.blackoutPeriodsTable.goToYear(moment(blackoutPeriod.startDate).year());

            this.analyticService.trackEvent(AnalyticEvents.TimeOffSettingsEditOrCreateWorkSchedule);
            this.notify.success({
                key: 'time-off-v3.blackoutPeriodActionedSuccessfully',
                params: { action: blackoutPeriodForm.isUpdate ? 'updated' : 'created' },
            });

            // Reset update state
            blackoutPeriodForm.isUpdate = false;
        } catch (e) {
            this.notify.error(e.message);
        }
    }

    private handleUpdateMenuAction(
        blackoutPeriodForm: BlackoutPeriodForm,
        blackoutPeriod: TimeOffRequestBlackoutPeriod
    ): void {
        blackoutPeriodForm.timeOffRequestBlackoutPeriod = blackoutPeriod;
        blackoutPeriodForm.isUpdate = true;
        this.blackoutPeriodModal.show();
    }

    private async handleDeleteMenuAction(blackoutPeriod: TimeOffRequestBlackoutPeriod): Promise<void> {
        const approved = await this.blackoutPeriodDeleteModal.show();

        if (!approved) {
            return;
        }

        try {
            await blackoutPeriod.delete();
            this.blackoutPeriodsTable.reload();
            this.notify.success('time-off-v3.blackoutPeriodDeletedSuccessfully');
        } catch (e) {
            this.notify.error(e.message);
        }
    }
}
